import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MessageCircle, BookOpen, Check, ChevronRight, X, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import BooksCountCard from "../components/BooksCountCard";

const LandingPage = () => {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("session_expired") === "1") {
      sessionStorage.removeItem("session_expired");
      setSessionExpiredMsg(true);
      setTimeout(() => setSessionExpiredMsg(false), 6000);
    }
  }, []);

  useEffect(() => {
    if (!loading && user) {
      if (user.profile_completed) {
        navigate('/dashboard');
      } else {
        navigate('/setup-profilo');
      }
    }
  }, [user, loading, navigate]);

  // Evita il flash della landing (con i pulsanti "Accedi") quando riapri l'app
  // da loggato: finché verifichiamo la sessione, o se sei già autenticato e
  // stiamo per reindirizzarti, mostra uno spinner invece del contenuto.
  if (loading || user) {
    return (
      <div
        className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"
        style={{ fontFamily: "'Outfit', sans-serif" }}
        data-testid="landing-loading"
      >
        <div className="w-12 h-12 border-4 border-[#FDE047] border-t-[#0A0A0A] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {sessionExpiredMsg && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[120] bg-[#0A0A0A] text-white border-2 border-[#FDE047] font-bold text-sm px-6 py-3 rounded-lg shadow-[4px_4px_0px_#FDE047] flex items-center gap-3 max-w-md" data-testid="session-expired-toast">
          <Info size={20} className="text-[#FDE047] flex-shrink-0" />
          <span>Sessione scaduta: hai effettuato l'accesso da un altro dispositivo.</span>
          <button onClick={() => setSessionExpiredMsg(false)} className="ml-2 flex-shrink-0 hover:opacity-70"><X size={16} /></button>
        </div>
      )}
      <header className="sticky top-0 z-50 bg-[#FDFBF7] border-b-4 border-[#0A0A0A] px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="text-lg md:text-2xl font-black tracking-tighter text-[#0A0A0A] flex-shrink-0" data-testid="logo">
            LIBRIVICINI
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#come-funziona" className="font-bold text-[#0A0A0A] hover:text-[#FDE047] transition-colors" data-testid="nav-how-it-works">
              Come funziona
            </a>
            <a href="#prezzi" className="font-bold text-[#0A0A0A] hover:text-[#FDE047] transition-colors" data-testid="nav-pricing">
              Prezzi
            </a>
            <Link to="/chi-sono" className="font-bold text-[#0A0A0A] hover:text-[#FDE047] transition-colors" data-testid="nav-about">
              Chi sono
            </Link>
          </nav>
          <button 
            onClick={login}
            className="bg-[#FDE047] text-[#0A0A0A] border-2 border-[#0A0A0A] font-bold uppercase tracking-wide px-3 py-2 md:px-6 md:py-3 text-xs md:text-sm shadow-[3px_3px_0px_#0A0A0A] md:shadow-[4px_4px_0px_#0A0A0A] hover:bg-yellow-300 transition-transform active:translate-y-1 active:translate-x-1 active:shadow-none flex-shrink-0"
            data-testid="header-login-btn"
          >
            <span className="hidden sm:inline">Accedi</span>
            <span className="sm:hidden">Accedi</span>
          </button>
        </div>
      </header>

      <section className="px-6 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          <div className="flex-1 text-left">
            <h1 className="text-5xl lg:text-7xl font-black tracking-tighter uppercase text-[#0A0A0A] leading-none mb-6" data-testid="hero-title">
              SCAMBIA.<br />VENDI.<br />SORRIDI.
            </h1>
            <p className="text-lg lg:text-xl text-[#0A0A0A] font-medium mb-8 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              Carica i libri di scuola usati su LibriVicini. Scegli se scambiarli o venderli e, se vendi, il prezzo lo decidi tu.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={login}
                className="bg-[#FDE047] text-[#0A0A0A] border-2 border-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 shadow-[4px_4px_0px_#0A0A0A] hover:bg-yellow-300 transition-transform active:translate-y-1 active:translate-x-1 active:shadow-none flex items-center justify-center gap-2"
                data-testid="hero-login-btn"
              >
                Inizia ora
              </button>
            </div>
          </div>
          <div className="flex-1 flex justify-center">
            <img 
              src="/hero-illustration.png"
              alt="Libri scolastici"
              width={1280}
              height={896}
              className="w-full max-w-md lg:max-w-lg h-auto"
              style={{ aspectRatio: "1280 / 896" }}
              data-testid="hero-illustration"
            />
          </div>
        </div>
      </section>

      <div className="bg-[#FDE047] border-y-4 border-[#0A0A0A] py-4 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex items-center gap-8">
          <span className="font-bold text-[#0A0A0A] uppercase tracking-wider">STUDENTI ITALIANI</span>
          <span className="text-[#0A0A0A]">&bull;</span>
          <span className="font-bold text-[#0A0A0A] uppercase tracking-wider">NESSUNA COMMISSIONE</span>
          <span className="text-[#0A0A0A]">&bull;</span>
          <span className="font-bold text-[#0A0A0A] uppercase tracking-wider">SCAMBI LOCALI</span>
          <span className="text-[#0A0A0A]">&bull;</span>
          <span className="font-bold text-[#0A0A0A] uppercase tracking-wider">STUDENTI ITALIANI</span>
          <span className="text-[#0A0A0A]">&bull;</span>
          <span className="font-bold text-[#0A0A0A] uppercase tracking-wider">NESSUNA COMMISSIONE</span>
          <span className="text-[#0A0A0A]">&bull;</span>
          <span className="font-bold text-[#0A0A0A] uppercase tracking-wider">SCAMBI LOCALI</span>
          <span className="text-[#0A0A0A]">&bull;</span>
        </div>
      </div>

      <section id="come-funziona" className="px-6 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto mb-16 lg:mb-20">
          <BooksCountCard variant="landing" onCta={login} />
        </div>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A0A0A] text-center mb-12" data-testid="how-it-works-title">Come Funziona</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#A7F3D0] border-4 border-[#0A0A0A] rounded-xl p-8 shadow-[8px_8px_0px_#0A0A0A]" data-testid="step-1-card">
              <div className="w-16 h-16 bg-[#FFFFFF] border-2 border-[#0A0A0A] rounded-full flex items-center justify-center mb-6 shadow-[4px_4px_0px_#0A0A0A]"><Search size={28} className="text-[#0A0A0A]" /></div>
              <h3 className="text-2xl lg:text-3xl font-bold text-[#0A0A0A] mb-4">1. Trova</h3>
              <p className="text-[#0A0A0A] font-medium leading-relaxed">Cerca per ISBN e visualizza i risultati ordinati per distanza dal tuo CAP.</p>
            </div>
            <div className="bg-[#D8B4E2] border-4 border-[#0A0A0A] rounded-xl p-8 shadow-[8px_8px_0px_#0A0A0A]" data-testid="step-2-card">
              <div className="w-16 h-16 bg-[#FFFFFF] border-2 border-[#0A0A0A] rounded-full flex items-center justify-center mb-6 shadow-[4px_4px_0px_#0A0A0A]"><BookOpen size={28} className="text-[#0A0A0A]" /></div>
              <h3 className="text-2xl lg:text-3xl font-bold text-[#0A0A0A] mb-4">2. Verifica</h3>
              <p className="text-[#0A0A0A] font-medium leading-relaxed">Controlla le condizioni esatte (sottolineature, note, copertina).</p>
            </div>
            <div className="bg-[#FDE047] border-4 border-[#0A0A0A] rounded-xl p-8 shadow-[8px_8px_0px_#0A0A0A]" data-testid="step-3-card">
              <div className="w-16 h-16 bg-[#FFFFFF] border-2 border-[#0A0A0A] rounded-full flex items-center justify-center mb-6 shadow-[4px_4px_0px_#0A0A0A]"><MessageCircle size={28} className="text-[#0A0A0A]" /></div>
              <h3 className="text-2xl lg:text-3xl font-bold text-[#0A0A0A] mb-4">3. Scambia</h3>
              <p className="text-[#0A0A0A] font-medium leading-relaxed">Chatta, organizzatevi e scambiate il libro di persona.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="prezzi" className="px-6 py-16 lg:py-24 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A0A0A] text-center mb-4" data-testid="pricing-title">Gratis. Per tutti.</h2>
          <p className="text-center text-[#0A0A0A] font-medium mb-12 max-w-xl mx-auto">
            LibriVicini è un progetto senza scopo di lucro: nessun abbonamento, nessun costo nascosto. Tutte le funzioni sono incluse e gratuite.
          </p>
          <div className="max-w-md mx-auto">
            <div className="bg-[#A7F3D0] border-4 border-[#0A0A0A] rounded-xl p-6 md:p-8 shadow-[8px_8px_0px_#0A0A0A]" data-testid="pricing-free">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-1 md:mb-2">Tutto incluso</h3>
              <div className="text-5xl md:text-6xl font-black text-[#0A0A0A] mb-6">&euro;0</div>
              <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                <li className="flex items-center gap-2 md:gap-3"><Check size={18} className="text-[#0A0A0A] flex-shrink-0" /><span className="font-medium text-[#0A0A0A] text-sm md:text-base">Fino a 36 libri pubblicati</span></li>
                <li className="flex items-center gap-2 md:gap-3"><Check size={18} className="text-[#0A0A0A] flex-shrink-0" /><span className="font-medium text-[#0A0A0A] text-sm md:text-base">3 foto per libro</span></li>
                <li className="flex items-center gap-2 md:gap-3"><Check size={18} className="text-[#0A0A0A] flex-shrink-0" /><span className="font-medium text-[#0A0A0A] text-sm md:text-base">Ricerca per ISBN e per scuola</span></li>
                <li className="flex items-center gap-2 md:gap-3"><Check size={18} className="text-[#0A0A0A] flex-shrink-0" /><span className="font-medium text-[#0A0A0A] text-sm md:text-base">Chat e notifiche match incluse</span></li>
              </ul>
              <button onClick={login} className="w-full bg-[#0A0A0A] text-[#A7F3D0] border-2 border-[#0A0A0A] font-bold uppercase tracking-wide px-4 py-3 md:px-6 md:py-4 text-sm shadow-[3px_3px_0px_#0A0A0A] md:shadow-[4px_4px_0px_#0A0A0A] hover:bg-gray-900 transition-transform active:translate-y-1 active:translate-x-1 active:shadow-none" data-testid="pricing-free-btn">Inizia Gratis</button>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#0A0A0A] text-[#FDFBF7] py-16 px-6 border-t-4 border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-4xl lg:text-5xl font-extrabold tracking-tight mb-4" data-testid="footer-cta">INIZIA A SCAMBIARE.</h2>
              <button onClick={login} className="bg-[#FDE047] text-[#0A0A0A] border-2 border-[#FDE047] font-bold uppercase tracking-wide px-8 py-4 shadow-[4px_4px_0px_#FDE047] hover:bg-yellow-300 transition-transform active:translate-y-1 active:translate-x-1 active:shadow-none flex items-center gap-2" data-testid="footer-login-btn">
                Inizia ora <ChevronRight size={20} />
              </button>
            </div>
            <div className="flex flex-wrap gap-6 lg:gap-8 text-sm font-bold uppercase tracking-wider">
              <Link to="/chi-sono" className="hover:text-[#FDE047] transition-colors" data-testid="footer-about">Chi sono</Link>
              <Link to="/privacy" className="hover:text-[#FDE047] transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/termini" className="hover:text-[#FDE047] transition-colors" data-testid="footer-terms">Termini</Link>
              <Link to="/cookie" className="hover:text-[#FDE047] transition-colors" data-testid="footer-cookie">Cookie</Link>
              <a href="mailto:info@librivicini.it" className="hover:text-[#FDE047] transition-colors" data-testid="footer-contact">Contatti</a>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-[#333] text-center text-sm text-gray-500">&copy; 2026 LibriVicini. Tutti i diritti riservati.</div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
